import { z } from 'zod';

/**
 * A job's timeline (OPS-54, fleet-agent ADR 0004 row B1): what a round says
 * about the job it holds, in order — the replacement for its Buzz thread.
 *
 * `claimed` opens the round (ticket, title, url, engine, machine in `fields`),
 * `progress` and `note` are free text, `phase` a lane state change, and `done`
 * / `dead-letter` its terminal line. `dead-letter` is the one that needs a
 * person: the page marks it.
 */
export const FLEET_JOB_EVENT_KINDS = ['claimed', 'progress', 'phase', 'done', 'dead-letter', 'note'] as const;
export const FleetJobEventKindSchema = z.enum(FLEET_JOB_EVENT_KINDS);
export type FleetJobEventKind = (typeof FLEET_JOB_EVENT_KINDS)[number];

/** Stored text is cut to this length, never refused — one long line must not cost the round its narration. */
export const FLEET_JOB_EVENT_TEXT_MAX_CHARS = 4000;

/** A request's text above this is refused outright: nothing a round narrates is this long. */
const FLEET_JOB_EVENT_TEXT_HARD_MAX_CHARS = 100_000;

/** The serialised `fields` object may not exceed this — it is structure, not a second text body. */
export const FLEET_JOB_EVENT_FIELDS_MAX_CHARS = 4000;

/** The page reads at most this many events per job, the most recent ones. */
export const FLEET_JOB_EVENT_READ_LIMIT = 500;

export const FleetJobEventFieldsSchema = z
  .record(z.string(), z.unknown())
  .refine((fields) => JSON.stringify(fields).length <= FLEET_JOB_EVENT_FIELDS_MAX_CHARS, {
    message: `fields must serialise to at most ${FLEET_JOB_EVENT_FIELDS_MAX_CHARS} characters`,
  });

export type FleetJobEventFields = z.infer<typeof FleetJobEventFieldsSchema>;

export const AppendFleetJobEventBodySchema = z.object({
  /** The epoch the claim returned — only the round holding it may narrate the job. */
  claimEpoch: z.number().int().positive(),
  kind: FleetJobEventKindSchema,
  text: z.string().min(1).max(FLEET_JOB_EVENT_TEXT_HARD_MAX_CHARS),
  /** Structured detail the kind carries — e.g. `ticket`, `url`, `engine` on `claimed`; `state` on `phase`. */
  fields: FleetJobEventFieldsSchema.optional(),
});

export type AppendFleetJobEventBody = z.infer<typeof AppendFleetJobEventBodySchema>;

export const AppendFleetJobEventResponseSchema = z.object({ id: z.uuid(), at: z.iso.datetime() });

export type AppendFleetJobEventResponse = z.infer<typeof AppendFleetJobEventResponseSchema>;

export const FleetJobEventViewSchema = z.object({
  id: z.uuid(),
  /** The server's clock when the event landed. */
  at: z.iso.datetime(),
  kind: FleetJobEventKindSchema,
  text: z.string(),
  fields: z.record(z.string(), z.unknown()).nullable(),
});

export type FleetJobEventView = z.infer<typeof FleetJobEventViewSchema>;

/** Oldest first — the newest event is the last one. */
export const ListFleetJobEventsResponseSchema = z.object({ data: z.array(FleetJobEventViewSchema) });

export type ListFleetJobEventsResponse = z.infer<typeof ListFleetJobEventsResponseSchema>;
